import { z } from 'zod';

import { gql } from '~/utils/gql';

export const marketSegmentSchema = z.enum(['accenture', 'deci', 'government', 'enterprise', 'scholarship', 'consumer']);
export const userExperienceSchema = z
  .object({
    data: z.object({
      user: z.object({
        subscriptions: z.object({
          all_access: z.boolean(),
          teams: z.boolean().nullable(),
        }),
        classroom: z.object({
          communities: z
            .array(
              z.object({
                url: z.string(),
                title: z.string(),
              })
            )
            .optional(),
          hasFocused: z.boolean(),
          supportUrl: z.string(),
          settings: z.object({
            emailEditDisabled: z.boolean().nullable(),
            nameEditDisabled: z.boolean().nullable(),
          }),
        }),
        knowledge: z.object({
          hasKnowledge: z.boolean(),
        }),
        assessments: z.object({
          workeraAccess: z.object({
            contractId: z.string(),
            companyId: z.string(),
            companySlug: z.string(),
            accessLevel: z.enum(['ALL_ACCESS', 'DISCOVERY_AND_FLUENCY', 'SUSPENDED', 'EXPIRED', '']),
          }),
        }),
        gtm: z.object({
          accenture: z.boolean(),
          deci: z.boolean(),
          government: z.boolean(),
          enterprise: z.boolean(),
          scholarship: z.boolean(),
          consumer: z.boolean(),
        }),
      }),
    }),
  })
  .transform(({ data }) => {
    const communities = data.user.classroom.communities || [];

    return {
      hasActiveSubscription: Boolean(data.user.subscriptions.all_access || data.user.subscriptions.teams),
      allAccess: data.user.subscriptions.all_access,
      teams: Boolean(data.user.subscriptions.teams),
      hasFocused: data.user.classroom.hasFocused,
      supportUrl: data.user.classroom.supportUrl,
      emailEditDisabled: Boolean(data.user.classroom.settings.emailEditDisabled),
      nameEditDisabled: Boolean(data.user.classroom.settings.nameEditDisabled),
      communities,
      hasKnowledge: data.user.knowledge.hasKnowledge,
      skillAssessments: data.user.assessments,
      marketSegments: data.user.gtm as Record<MarketSegment, boolean>,
    };
  });

export type UserExperience = z.infer<typeof userExperienceSchema>;
export type UserExperienceResponse = z.input<typeof userExperienceSchema>;
export type WorkeraAccessLevel = 'ALL_ACCESS' | 'DISCOVERY_AND_FLUENCY' | 'SUSPENDED' | 'EXPIRED' | '';
export type MarketSegment = z.infer<typeof marketSegmentSchema>;
export type Community = {
  url: string;
  title: string;
};

export const userExperienceQuery = gql`
  query FetchUserExperience($uid: String!) {
    user(id: $uid) {
      subscriptions {
        all_access
        teams
      }
      classroom {
        communities {
          url
          title
        }
        hasFocused
        supportUrl
        settings {
          emailEditDisabled
          nameEditDisabled
        }
      }
      knowledge {
        hasKnowledge
      }
      assessments {
        workeraAccess {
          contractId
          companyId
          companySlug
          accessLevel
        }
      }
      gtm {
        accenture
        deci
        government
        enterprise
        scholarship
        consumer
      }
    }
  }
`;
